import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import {
  AutoAwesome as DraftIcon,
  RateReview as ReviewIcon,
  ThumbUp as ApprovedIcon,
  Send as SubmittedIcon,
  CheckCircle as AcceptedIcon,
  Cancel as CancelledIcon,
  Block as RejectedIcon,
  Edit as AmendedIcon,
  Paid as PaidIcon,
  Schedule as PendingIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Shared Tax & Statutory UI kit - mirrors budgeting/BudgetingShared.js and
// the other ledger kits so Compliance reads as one product with the rest of
// the ledger. Lifecycles verified against accounting-apis tax controllers.
// ---------------------------------------------------------------------------

export const getErrorMessage = (error, fallback = 'Operation failed') => {
  const data = error?.response?.data;
  const message = data?.message || fallback;
  const details = data?.errors;
  if (!details) return message;
  const flat = Array.isArray(details)
    ? details.map((d) => (typeof d === 'string' ? d : JSON.stringify(d)))
    : Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
  return flat.length ? `${message}: ${flat.join('; ')}` : message;
};

export const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

export const unwrapList = (response) => {
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : payload.items ?? [];
};

export const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? String(d)
    : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const RETURN_STATUS_CFG = {
  DRAFT: { label: 'Draft', color: 'default', Icon: DraftIcon },
  IN_REVIEW: { label: 'In Review', color: 'info', Icon: ReviewIcon },
  APPROVED: { label: 'Approved', color: 'success', Icon: ApprovedIcon },
  SUBMITTED: { label: 'Submitted', color: 'info', Icon: SubmittedIcon },
  ACCEPTED: { label: 'Accepted', color: 'success', Icon: AcceptedIcon },
  REJECTED: { label: 'Rejected', color: 'error', Icon: RejectedIcon },
  AMENDED: { label: 'Amended', color: 'warning', Icon: AmendedIcon },
  CANCELLED: { label: 'Cancelled', color: 'default', Icon: CancelledIcon },
};

export const TaxReturnStatusChip = ({ status }) => {
  const st = String(status || 'DRAFT').toUpperCase();
  const cfg = RETURN_STATUS_CFG[st] || { label: status || 'Draft', color: 'default', Icon: DraftIcon };
  const { Icon } = cfg;
  return <Chip icon={<Icon fontSize="small" />} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

const PAYMENT_STATUS_CFG = {
  UNPAID: { label: 'Unpaid', color: 'error', Icon: PendingIcon },
  PARTIALLY_PAID: { label: 'Partially Paid', color: 'warning', Icon: PendingIcon },
  PAID: { label: 'Paid', color: 'success', Icon: PaidIcon },
  OVERPAID: { label: 'Overpaid', color: 'info', Icon: PaidIcon },
};

export const TaxPaymentStatusChip = ({ status }) => {
  const st = String(status || 'UNPAID').toUpperCase();
  const cfg = PAYMENT_STATUS_CFG[st] || { label: status || 'Unpaid', color: 'default', Icon: PendingIcon };
  const { Icon } = cfg;
  return <Chip icon={<Icon fontSize="small" />} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

const WHT_CERT_CFG = {
  PENDING: { label: 'Pending', color: 'default', Icon: PendingIcon },
  ISSUED: { label: 'Issued', color: 'info', Icon: SubmittedIcon },
  SUBMITTED: { label: 'Submitted', color: 'info', Icon: SubmittedIcon },
  VERIFIED: { label: 'Verified', color: 'success', Icon: VerifiedIcon },
  EXPIRED: { label: 'Expired', color: 'warning', Icon: PendingIcon },
  CANCELLED: { label: 'Cancelled', color: 'default', Icon: CancelledIcon },
};

export const WhtCertStatusChip = ({ status }) => {
  const st = String(status || 'PENDING').toUpperCase();
  const cfg = WHT_CERT_CFG[st] || { label: status || 'Pending', color: 'default', Icon: PendingIcon };
  const { Icon } = cfg;
  return <Chip icon={<Icon fontSize="small" />} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

const WHT_REMIT_CFG = {
  PENDING: { label: 'Pending', color: 'default', Icon: PendingIcon },
  SUBMITTED: { label: 'Submitted', color: 'info', Icon: SubmittedIcon },
  PAID: { label: 'Paid', color: 'success', Icon: PaidIcon },
  CONFIRMED: { label: 'Confirmed', color: 'success', Icon: VerifiedIcon },
  REJECTED: { label: 'Rejected', color: 'error', Icon: RejectedIcon },
};

export const WhtRemitStatusChip = ({ status }) => {
  const st = String(status || 'PENDING').toUpperCase();
  const cfg = WHT_REMIT_CFG[st] || { label: status || 'Pending', color: 'default', Icon: PendingIcon };
  const { Icon } = cfg;
  return <Chip icon={<Icon fontSize="small" />} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

export const StatTile = ({ label, value, color }) => (
  <Box sx={{ flex: '1 1 140px', textAlign: 'center', py: 1.5, px: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight={700} color={color} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

export const StatStrip = ({ tiles = [] }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    divider={<Divider orientation="vertical" flexItem />}
    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}
  >
    {tiles.map((t) => (
      <StatTile key={t.label} {...t} />
    ))}
  </Stack>
);

export const taxReturnIdOf = (r) => r?.return_id ?? r?.id;
export const taxLineIdOf = (l) => l?.line_id ?? l?.id;
export const whtCertIdOf = (c) => c?.certificate_id ?? c?.id;
export const whtRemitIdOf = (r) => r?.remittance_id ?? r?.id;

export const RETURN_TYPES = ['VAT', 'WITHHOLDING_TAX', 'CORPORATE_TAX', 'PAYE', 'EXCISE_DUTY', 'IMPORT_DUTY', 'OTHER'].map((t) => ({ label: t.replace(/_/g, ' '), value: t }));
export const RETURN_STATUSES = ['DRAFT', 'IN_REVIEW', 'APPROVED', 'SUBMITTED', 'ACCEPTED', 'REJECTED', 'AMENDED', 'CANCELLED'];
export const PAYMENT_STATUSES = ['UNPAID', 'PARTIALLY_PAID', 'PAID', 'OVERPAID'];
export const LINE_TYPES = ['INPUT_VAT', 'OUTPUT_VAT', 'VAT_ADJUSTMENT', 'WHT_PAYABLE', 'WHT_RECEIVABLE', 'PAYE', 'CORPORATE_TAX', 'PENALTY', 'INTEREST', 'OTHER'].map((t) => ({ label: t.replace(/_/g, ' '), value: t }));
export const SUBMISSION_METHODS = ['MANUAL', 'PORTAL', 'API', 'IMPORT'].map((t) => ({ label: t, value: t }));
